/**
 * Fan-paneel in de chat: de tabbladen Transactions en History zoals op het
 * oude dashboard (15-10-2026, Jordy). De rekenregels staan in lib/fanPaneel.ts
 * (getest met node); hier alleen de weergave.
 *
 *   FanTransacties   chips (subscribed, tips, messages, spent), Spent on,
 *                    Tips vs PPV met Media unlocks, Latest transactions met
 *                    datum en soort.
 *   FanHistorie      Unlock history (altijd zichtbaar), Content history
 *                    (betaald én gratis, gekocht of niet) met Resend.
 *
 * Overgenomen uit tak chat/fanpaneel (08-10, niet gemerged), zonder de kolom
 * Model: die kwam uit een backendwijziging van die tak, en oud toonde hem niet.
 */
import { useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Modal } from './Modal'
import { Table, Tag } from './ui'
import { api, chatMediaUrl, thumbUrl } from '../lib/api'
import { useActions } from '../lib/actions'
import { dtSec, eur, num } from '../lib/format'
import { PPV_MIN_F2F_EUR, PPV_MIN_FC_CREDITS, isF2f } from '../lib/massPrijs'
import {
  duurSinds, fooien, inhoudLabel, mediaSoortTekst, prijsTekst, resendPrijsFout, schoneTekst,
  tipsTegenPpv, uitgegevenAan, unlockRegels, verstuurOpnieuw,
} from '../lib/fanPaneel'
import type { Bedrag, Transactie, Verzending } from '../lib/fanPaneel'

/** Een tijd zonder zone is UTC (zelfde regel als het chatvenster). */
const utc = (v: unknown): string | null => {
  if (!v) return null
  const s = String(v)
  return /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s.replace(' ', 'T')}Z`
}
const wanneer = (v: unknown) => (v ? dtSec(utc(v)) : '—')

const kop = { display: 'block', fontWeight: 700, fontSize: 12.5, marginBottom: 6 } as const
const klein = { fontSize: 11.5 } as const

/** Wat dit paneel van het gesprek leest (velden van GET /conversations/{id}). */
export interface PaneelGesprek {
  id: number
  platform: string
  total_messages: number
  fan_total_spend: number
  fan_subscription_status: string | null
  fan_subscription_since: string | null
  fan_spend_by_category?: Record<string, Bedrag> | null
  fan_spend_breakdown?: Record<string, Bedrag> | null
  fan_spend_mix: Record<string, unknown> | null
  fan_recent_transactions: Transactie[] | null
  fan_content_sends?: Verzending[] | null
}

// ── Transactions ───────────────────────────────────────────────────────────

function Regel({ links, rechts, dim }: { links: string; rechts: string; dim?: boolean }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', gap: 8, fontSize: 12.5 }}>
      <span className={dim ? 'mut2' : undefined}>{links}</span>
      <b className="mono" style={{ color: dim ? undefined : '#fbbf24' }}>{rechts}</b>
    </div>
  )
}

export function FanTransacties({ c }: { c: PaneelGesprek }) {
  const tips = fooien(c)
  const spent = uitgegevenAan(c)
  const mix = tipsTegenPpv(c)
  const tx = c.fan_recent_transactions ?? []
  return (
    <div data-testid="fan-transactions">
      <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <Tag kind={String(c.fan_subscription_status).toLowerCase() === 'active' ? 'ok' : 'mut'}>
          {duurSinds(c.fan_subscription_since)} subscribed
        </Tag>
        <Tag kind="mut">{tips === null ? 'No tips recorded' : `${eur(tips)} tips`}</Tag>
        <Tag kind="mut">{Number(c.total_messages || 0).toLocaleString('en-US')} messages</Tag>
        <Tag kind={c.fan_total_spend > 0 ? 'warn' : 'mut'}>{eur(c.fan_total_spend)} spent</Tag>
      </div>

      {spent.length ? (
        <div className="subblock" style={{ marginBottom: 10 }} data-testid="spent-on">
          <span style={kop}>Spent on</span>
          {spent.map((r) => (
            <Regel key={r.label} dim={r.label === 'Not itemised'}
                   links={`${r.label}${r.count > 0 ? ` · ${r.count}×` : ''}${r.pct !== null ? ` · ${r.pct}%` : ''}`}
                   rechts={eur(r.amount)} />
          ))}
        </div>
      ) : null}

      {mix ? (
        <div className="subblock" style={{ marginBottom: 10 }} data-testid="tips-vs-ppv">
          <span style={kop}>Tips vs PPV</span>
          <Regel links="Tips"
                 rechts={`${eur(mix.tips.amount)} · ${mix.tips.pct}%${mix.tips.count ? ` · ${mix.tips.count}×` : ''}`} />
          <Regel links="PPV / unlocks"
                 rechts={`${eur(mix.ppv.amount)} · ${mix.ppv.pct}%${mix.ppv.count ? ` · ${mix.ppv.count}×` : ''}`} />
          {mix.subscriptions.amount > 0.005
            ? <Regel links="Subscriptions"
                     rechts={`${eur(mix.subscriptions.amount)} · ${mix.subscriptions.count}×`} />
            : null}
          {mix.typering ? <div className="mut2" style={{ ...klein, fontWeight: 700, marginTop: 4 }}>{mix.typering}</div> : null}
          {mix.media.length ? (
            <>
              <span style={{ ...kop, marginTop: 8 }}>Media unlocks</span>
              {mix.media.map((m) => (
                <Regel key={m.label} links={`${m.label}${m.count ? ` · ${m.count}×` : ''}`} rechts={eur(m.amount)} />
              ))}
              <div className="mut2" style={{ fontSize: 10.5, marginTop: 4 }}>
                Platform reports Video unlocks and Chat media separately. Chat media can be photo or video.
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      <span style={kop}>Latest transactions</span>
      <Table
        cols={[
          { key: 'd', label: 'Date' }, { key: 'w', label: 'What' }, { key: 't', label: 'Type' },
          { key: 'a', label: 'Amount', num: true },
        ]}
        emptyText="No transactions for this fan."
      >
        {tx.map((t, i) => (
          <tr key={`${t.at ?? ''}-${t.amount ?? 0}-${i}`}>
            <td className="mono" style={{ whiteSpace: 'nowrap' }}>{wanneer(t.at)}</td>
            <td>{String(t.label || t.event_type || 'Purchase')}</td>
            <td className="mut">{mediaSoortTekst(t.media_kind)}</td>
            <td className="num mono">{eur(t.amount)}</td>
          </tr>
        ))}
      </Table>
      <div className="mut2" style={{ ...klein, fontStyle: 'italic', marginTop: 6 }}>
        Cross-model total, highest/lowest paid and subscription lines are on the Fan info tab.
      </div>
    </div>
  )
}

// ── History ────────────────────────────────────────────────────────────────

function Miniatuur({ v }: { v: Verzending }) {
  const id = Number(v.content_item_id || 0)
  const bron = id > 0 ? thumbUrl(id) : v.media_url ? chatMediaUrl(v.media_url) : null
  return (
    <div style={{
      width: 44, height: 44, borderRadius: 8, flex: 'none', background: '#0f172a',
      border: '1px solid var(--line)', backgroundSize: 'cover', backgroundPosition: 'center',
      backgroundImage: bron ? `url("${bron}")` : undefined,
    }} />
  )
}

export function FanHistorie({ c, onSpring, onResent }: {
  c: PaneelGesprek
  /** Naar de bubbel van dit bericht in de chat springen. */
  onSpring: (messageId: number) => void
  onResent: () => void
}) {
  const { notify } = useActions()
  const sends = c.fan_content_sends ?? []
  const unlocks = unlockRegels(sends, c.fan_recent_transactions)
  const [open, setOpen] = useState<Verzending | null>(null)
  const [tekst, setTekst] = useState('')
  const [prijs, setPrijs] = useState('')
  const [eerstWeg, setEerstWeg] = useState(false)
  // Zelfde standaard als de content picker: het bijschrift gaat zoals getypt.
  const [alsGetypt, setAlsGetypt] = useState(true)
  const [fout, setFout] = useState('')
  const [bezig, setBezig] = useState(false)
  const f2f = isF2f(c.platform)

  const kies = (v: Verzending) => {
    setOpen(v)
    setTekst(schoneTekst(v.caption))
    setPrijs(v.is_paid && Number(v.price || 0) > 0 ? String(v.price) : '')
    setEerstWeg(!!v.can_retract_locally && !v.is_unlocked)
    setAlsGetypt(true)
    setFout('')
  }
  const verstuur = async () => {
    if (!open) return
    const prijsFout = resendPrijsFout(c.platform, prijs)
    if (prijsFout) { setFout(prijsFout); return }
    setBezig(true)
    setFout('')
    try {
      await verstuurOpnieuw(api, {
        conversationId: Number(c.id), platform: c.platform, item: open,
        tekst, prijs, eerstVerwijderen: eerstWeg, alsGetypt,
      })
      notify('Queued — look in the chat for the Sending… bubble. Large files can take a minute.')
      setOpen(null)
      onResent()
    } catch (e) {
      setFout(String((e as Error)?.message || e))
    } finally {
      setBezig(false)
    }
  }
  const klikbaar = (mid: number) => (mid > 0 ? {
    role: 'button' as const, tabIndex: 0,
    onClick: () => onSpring(mid),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSpring(mid) }
    },
    style: { cursor: 'pointer' },
  } : {})

  return (
    <div>
      {/* Unlock history staat er altijd, ook leeg: dan zegt hij dat. */}
      <div data-testid="unlock-history" style={{ marginBottom: 14 }}>
        <span style={kop}>Unlock history</span>
        {!unlocks.berichten.length && !unlocks.transacties.length ? (
          <div className="mut2" style={klein}>
            No unlocks recorded for this fan yet. Unlocks appear here as soon as the platform reports the purchase.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {unlocks.berichten.map((v) => (
              <div key={`u-${v.message_id}`} {...klikbaar(Number(v.message_id))}
                   className="subblock" title="Show this message in the chat">
                <b style={{ color: '#34d399', fontSize: 12.5 }}>
                  Unlocked {v.media_type || 'media'}{v.is_paid ? ` · ${prijsTekst(v.price, c.platform)}` : ''}
                </b>
                <div className="mut2" style={klein}>{wanneer(v.unlocked_at || v.created_at)} · show in chat</div>
              </div>
            ))}
            {unlocks.transacties.map((t, i) => (
              <div key={`t-${t.at ?? i}-${t.amount ?? 0}-${i}`} className="subblock">
                <Regel links={`${t.label || 'Unlock'}${t.media_kind ? ` · ${mediaSoortTekst(t.media_kind).toLowerCase()}` : ''}`}
                       rechts={eur(t.amount)} />
                <div className="mut2" style={klein}>{wanneer(t.at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid="content-history" style={{ marginBottom: 14 }}>
        <span style={kop}>Content history</span>
        <div className="mut2" style={{ ...klein, marginBottom: 8 }}>
          Paid and free content previously sent in this chat, bought or not. Click a row to jump to it
          in the chat. Resend a library item with a new text or price.
        </div>
        {sends.length === 0 ? (
          <div className="mut2" style={klein}>No free or paid content sends recorded for this fan yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {sends.map((v) => {
              const mislukt = !!v.delivery_error || v.delivered === false
              return (
                <div key={`${v.message_id}-${v.bundle_id ?? ''}`} className="subblock"
                     data-testid="content-history-row"
                     style={{ display: 'flex', gap: 10, alignItems: 'center',
                              borderColor: mislukt ? 'var(--bad)' : undefined }}>
                  <div {...klikbaar(Number(v.message_id))}
                       style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 0,
                                cursor: Number(v.message_id) > 0 ? 'pointer' : 'default' }}>
                    <Miniatuur v={v} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="row" style={{ flexWrap: 'wrap', gap: 4, marginBottom: 2 }}>
                        <Tag kind={v.is_paid ? (v.is_unlocked ? 'ok' : 'warn') : 'mut'}>{inhoudLabel(v, c.platform)}</Tag>
                        {v.media_type ? <Tag kind="mut">{String(v.media_type)}</Tag> : null}
                        {mislukt ? <Tag kind="bad">{v.delivery_error ? 'Not delivered' : 'Sending failed'}</Tag> : null}
                      </div>
                      <div className="mut2" style={{ ...klein, whiteSpace: 'nowrap', overflow: 'hidden',
                                                     textOverflow: 'ellipsis' }}>
                        {wanneer(v.created_at)}{v.caption ? ` · ${v.caption}` : ''}
                        {Number(v.message_id) > 0 ? ' · show in chat' : ''}
                      </div>
                    </div>
                  </div>
                  <button className={mislukt ? 'btn primary sm' : 'btn ghost sm'} type="button"
                          data-testid="content-resend"
                          disabled={!v.can_resend}
                          title={v.can_resend ? 'Resend at a new price'
                                              : 'No library id — re-pick from content library'}
                          onClick={() => kies(v)}>
                    Resend
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {open ? (
        <Modal
          title="Resend content"
          desc={<>
            Same media from the library. You can change the message text and the price
            {f2f ? ' (€)' : ' (credits)'}. If the old send was too low and not bought, tick delete
            first, then send higher.
            {open.delivery_error || open.delivered === false
              ? ` The previous send never reached the fan${open.delivery_error ? ` (${open.delivery_error})` : ''} — you can clear it and try again.`
              : ''}
          </>}
          width={480}
          onClose={() => !bezig && setOpen(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button" disabled={bezig}
                      onClick={() => setOpen(null)}>Cancel</button>
              <button className="btn primary sm" type="button" data-testid="resend-send"
                      disabled={bezig} onClick={verstuur}>
                {bezig ? 'Sending…' : 'Send again'}
              </button>
            </>
          }
        >
          <div className="field">
            <label>Message text</label>
            <textarea className="inp" rows={2} value={tekst} disabled={bezig}
                      placeholder="Optional text sent with the media…"
                      onChange={(e) => setTekst(e.target.value)} />
          </div>
          <div className="field">
            <label>{f2f ? 'Price (€, 0 = free)' : 'Price (credits, 0 = free)'}</label>
            <input className="inp" type="number" min={0} step={f2f ? 0.5 : 1} value={prijs}
                   disabled={bezig} onChange={(e) => setPrijs(e.target.value)} />
            <span className="hint">
              Paid content: at least {f2f ? `€${PPV_MIN_F2F_EUR}` : `${num(PPV_MIN_FC_CREDITS)} credits`}.
            </span>
          </div>
          <label className="chkrow">
            <span className={eerstWeg ? 'chk on' : 'chk'}
                  onClick={() => { if (!bezig && !open.is_unlocked) setEerstWeg(!eerstWeg) }}>
              {eerstWeg ? '✓' : ''}
            </span>
            Delete the old message first
            {open.can_retract_locally ? ' (failed send — cleared from chat only)' : ' (only if not yet unlocked)'}
          </label>
          {f2f ? (
            <span className="mut2" style={{ fontSize: 11.5 }}>
              F2F: translations are always on — the text goes out in the fan’s language.
            </span>
          ) : (
            <label className="chkrow">
              <span className={alsGetypt ? 'chk on' : 'chk'}
                    onClick={() => { if (!bezig) setAlsGetypt(!alsGetypt) }}>
                {alsGetypt ? '✓' : ''}
              </span>
              Send caption as typed
            </label>
          )}
          {fout ? <div className="note" style={{ color: 'var(--bad)' }}>{fout}</div> : null}
        </Modal>
      ) : null}
    </div>
  )
}
